import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ApiError } from "@finora/api-client";
import { Skeleton } from "@finora/ui";
import { api } from "../../lib/api";
import { ModuleGateNotice } from "../../components/ModuleGateNotice";

const inputCls = "h-10 rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none";
const fmt = (n: number) => n.toLocaleString("sr-RS", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const qty = (n: number) => n.toLocaleString("sr-RS", { maximumFractionDigits: 3 });
const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "Europe/Belgrade" });
const monthStart = () => today().slice(0, 8) + "01";

type Tab = "artikli" | "kategorije" | "kasiri" | "sati";

function Table({ head, rows }: { head: string[]; rows: (string | number)[][] }) {
  if (!rows.length) return <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-500">Nema prodaje u izabranom periodu.</p>;
  return (
    <div className="overflow-x-auto rounded-xl border border-border bg-surface shadow-subtle">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-xs text-slate-500">
            {head.map((h, i) => <th key={h} className={"px-3 py-2 font-medium " + (i > 0 ? "text-right" : "")}>{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-border last:border-0">
              {r.map((c, j) => <td key={j} className={"px-3 py-1.5 " + (j > 0 ? "text-right tabular-nums" : "")}>{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function HorecaIzvestajiPage() {
  const [tab, setTab] = useState<Tab>("artikli");
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const rep = useQuery({ queryKey: ["horeca-sales", from, to], queryFn: () => api.getHorecaSalesReport({ from, to }), enabled: !!from && !!to && from <= to, retry: false });

  if (rep.isError) {
    if (rep.error instanceof ApiError && rep.error.response.errorCode === "MODULE_NOT_AVAILABLE") return <ModuleGateNotice error={rep.error} moduleKey="horeca" />;
    return <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">Učitavanje nije uspelo.</p>;
  }
  const d = rep.data;
  const tabs: [Tab, string][] = [["artikli", "Po artiklu"], ["kategorije", "Po kategoriji"], ["kasiri", "Po kasiru"], ["sati", "Po satu"]];
  const maxHour = Math.max(1, ...(d?.by_hour ?? []).map((h) => h.gross));

  return (
    <div className="print-area space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
        <h1 className="text-xl font-semibold text-slate-900">HoReCa izveštaji</h1>
        <p className="text-sm text-slate-500">Prodaja iz izdatih fiskalnih računa, sa PDV-om. Stornirano se oduzima.</p>
        <p className="hidden text-sm print:block">Period: {from.split("-").reverse().join(".")}. – {to.split("-").reverse().join(".")}.</p>
        </div>
        <button className="rounded-lg border border-border px-3 py-2 text-sm hover:bg-slate-50 print:hidden" onClick={() => window.print()}>Štampaj</button>
      </div>
      <div className="flex flex-wrap items-end gap-3 print:hidden">
        <label className="text-xs text-slate-500">Od<input className={inputCls + " block"} type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
        <label className="text-xs text-slate-500">Do<input className={inputCls + " block"} type="date" value={to} onChange={(e) => setTo(e.target.value)} /></label>
        <div className="flex flex-wrap gap-1 rounded-lg border border-border p-0.5">
          {tabs.map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} className={"rounded-md px-3 py-1.5 text-sm " + (tab === k ? "bg-brand text-white" : "text-slate-600")}>{l}</button>
          ))}
        </div>
      </div>
      {!d && <Skeleton className="h-32 w-full" />}
      {d && (
        <>
          <div className="flex flex-wrap gap-x-8 gap-y-1 rounded-xl border border-border bg-surface p-3 text-sm shadow-subtle">
            <span>Računa: <b>{d.receipts}</b></span>
            <span>Prodaja: <b>{fmt(d.sales_gross)}</b></span>
            <span>Storno: <b>{fmt(d.refunds_gross)}</b> ({d.refunds_count})</span>
            <span>Promet: <b>{fmt(d.turnover_gross)}</b></span>
          </div>
          {tab === "artikli" && <Table head={["Artikal", "Količina", "Iznos", "Popust"]} rows={d.by_article.map((a) => [a.name, qty(a.quantity), fmt(a.gross), fmt(a.discount)])} />}
          {tab === "kategorije" && <Table head={["Kategorija", "Količina", "Iznos"]} rows={d.by_category.map((c) => [c.name, qty(c.quantity), fmt(c.gross)])} />}
          {tab === "kasiri" && <Table head={["Kasir", "Računa", "Storno", "Iznos"]} rows={d.by_cashier.map((c) => [c.name ?? "—", c.receipts, c.refunds, fmt(c.gross)])} />}
          {tab === "sati" && (
            d.by_hour.length === 0 ? <Table head={[]} rows={[]} /> : (
              <div className="space-y-1 rounded-xl border border-border bg-surface p-3 shadow-subtle">
                {d.by_hour.map((h) => (
                  <div key={h.hour} className="flex items-center gap-2 text-sm">
                    <span className="w-14 shrink-0 text-slate-500">{String(h.hour).padStart(2, "0")}:00</span>
                    <div className="h-4 flex-1 rounded bg-slate-100"><div className="h-4 rounded bg-brand" style={{ width: `${Math.max(1, (Math.max(h.gross, 0) / maxHour) * 100)}%` }} /></div>
                    <span className="w-28 shrink-0 text-right tabular-nums">{fmt(h.gross)}</span>
                    <span className="w-16 shrink-0 text-right text-xs text-slate-500">{h.receipts} rač.</span>
                  </div>
                ))}
              </div>
            )
          )}
        </>
      )}
    </div>
  );
}
